import {
  Component,
  OnInit,
  AfterViewInit,
  OnDestroy,
  QueryList,
  ViewChildren,
  ChangeDetectorRef,
  ViewEncapsulation,
  ChangeDetectionStrategy,
  DestroyRef,
  effect,
  inject,
  Injector,
  input,
  signal,
  computed,
} from "@angular/core";
import { DomSanitizer } from "@angular/platform-browser";
import { MatChipSelectionChange } from "@angular/material/chips";
import { MatSnackBar } from "@angular/material/snack-bar";
import { StoreUserService } from '@app/core/state/store-user.service';
import {
  DatabaseCacheService,
  Section,
} from "../../../../data/cache/database-cache.service";
import { ListDialogsService } from "../../../../data/dialog/list-dialogs.service";
import { MsalService } from "@azure/msal-angular";
import { ActivatedRoute, Router } from "@angular/router";
import { lastValueFrom } from "rxjs";
import { CreateDialogService } from "../../../../data/dialog/create-dialog.service";
import { UpdateDialogService } from "../../../../data/dialog/update-dialog.service";
import { CreateMentionService } from "../../../../data/mention/create-mention.service";
import { ListMembersService } from "../../../../data/member/list-members.service";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";

import { BelongsDocument } from "../../../../data/models/belong.model";
import { DialogDocument, DialogStatus, MetaDialogUser } from "../../../../data/models/dialog.model";
import { SHARED_IMPORTS } from "../../../../shared/standalone-imports";
import { MentionTextareaComponent } from "../mention-textarea/mention-textarea.component";
import { EXPERT_DIALOG_I18N } from "../expert-dialog.i18n";
import { getTimestamp, parseDateInput } from "@app/core/date/date-time.util";
import { PopupWindowStateService } from "../popup-window/popup-window-state.service";

type MentionLinkKind = 'record' | 'media';

@Component({
  selector: "app-expert-dialog-inner",
  templateUrl: "./expert-dialog-inner.component.html",
  styleUrls: ["./expert-dialog-inner.component.scss"],
  encapsulation: ViewEncapsulation.Emulated,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [...SHARED_IMPORTS, MentionTextareaComponent],
  host: {
    '[class.popup-mode]': 'popupMode()',
  },
})
export class ExpertDialogInnerComponent implements OnInit, AfterViewInit, OnDestroy {
  readonly popupMode = input(false);
  private readonly destroyRef = inject(DestroyRef);
  private readonly storeUser = inject(StoreUserService);
  private readonly route = inject(ActivatedRoute);
  readonly databaseCashe = inject(DatabaseCacheService);
  private readonly listDialogsService = inject(ListDialogsService);
  private readonly createDialogService = inject(CreateDialogService);
  private readonly updateDialogService = inject(UpdateDialogService);
  private readonly createMentionService = inject(CreateMentionService);
  private readonly listMembersService = inject(ListMembersService);
  private readonly msalService = inject(MsalService);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);
  private readonly router = inject(Router);
  private readonly sanitizer = inject(DomSanitizer);
  private readonly snackBar = inject(MatSnackBar);
  private readonly popupWindowState = inject(PopupWindowStateService);
  private readonly injector = inject(Injector);

  protected readonly i18n = EXPERT_DIALOG_I18N;
  readonly ui = {
    currentMedia: $localize`:@@home.expertDialog.ui.currentMedia:現在のメディア`,
    currentRecord: $localize`:@@home.expertDialog.ui.currentRecord:現在のレコード`,
    mediaFallback: $localize`:@@home.expertDialog.ui.mediaFallback:メディア`,
    image: $localize`:@@home.expertDialog.ui.image:静止画`,
    video: $localize`:@@home.expertDialog.ui.video:動画`,
    record: $localize`:@@home.expertDialog.ui.record:記録`,
    newConsultation: $localize`:@@home.expertDialog.ui.newConsultation:新規相談を投稿`,
    search: $localize`:@@home.expertDialog.ui.search:検索...`,
    searchConsultation: $localize`:@@home.expertDialog.ui.searchConsultation:相談を検索`,
    clearSearch: $localize`:@@home.expertDialog.ui.clearSearch:検索をクリア`,
    filter: $localize`:@@home.expertDialog.ui.filter:フィルター`,
    openOnly: $localize`:@@home.expertDialog.ui.openOnly:相談中のみ`,
    consultationList: $localize`:@@home.expertDialog.ui.consultationList:相談一覧`,
    loading: $localize`:@@home.expertDialog.ui.loading:読み込み中`,
    loadingText: $localize`:@@home.expertDialog.ui.loadingText:読み込み中...`,
    statusOpen: $localize`:@@home.expertDialog.ui.statusOpen:相談中`,
    statusClosed: $localize`:@@home.expertDialog.ui.statusClosed:対応済み`,
    noConsultation: $localize`:@@home.expertDialog.ui.noConsultation:相談はまだありません`,
    consultationDetail: $localize`:@@home.expertDialog.ui.consultationDetail:相談詳細`,
    close: $localize`:@@home.expertDialog.ui.close:閉じる`,
    cancel: $localize`:@@home.expertDialog.ui.cancel:キャンセル`,
    composePlaceholder: $localize`:@@home.expertDialog.ui.composePlaceholder:相談内容を入力してください… @メンションや #記録・メディア が使用できます`,
    mentionCurrentRecord: $localize`:@@home.expertDialog.ui.mentionCurrentRecord:当レコードにメンション`,
    mentionCurrentMedia: $localize`:@@home.expertDialog.ui.mentionCurrentMedia:当メディアにメンション`,
    post: $localize`:@@home.expertDialog.ui.post:投稿する`,
    replyPlaceholder: $localize`:@@home.expertDialog.ui.replyPlaceholder:返信内容を入力してください…`,
    reply: $localize`:@@home.expertDialog.ui.reply:返信する`,
    edited: $localize`:@@home.expertDialog.ui.edited:(編集済み)`,
    selectConsultation: $localize`:@@home.expertDialog.ui.selectConsultation:相談を選択してください`,
    selectConsultationHelp: $localize`:@@home.expertDialog.ui.selectConsultationHelp:左のリストから相談を選択すると、会話が表示されます`,
    statusDone: $localize`:@@home.expertDialog.ui.statusDone:相談を完了にしました`,
    statusReopened: $localize`:@@home.expertDialog.ui.statusReopened:相談を進行中に戻しました`,
    undo: $localize`:@@home.expertDialog.ui.undo:元に戻す`,
    statusUpdateFailed: $localize`:@@home.expertDialog.ui.statusUpdateFailed:状態の更新に失敗しました`,
    markResolved: $localize`:@@home.expertDialog.ui.markResolved:完了する`,
    reopenConsultation: $localize`:@@home.expertDialog.ui.reopenConsultation:相談中に戻す`,
    errorNoPermission: $localize`:@@home.expertDialog.ui.errorNoPermission:（権限がありません）`,
    errorAuth: $localize`:@@home.expertDialog.ui.errorAuth:（認証エラー）`,
    errorNotFound: $localize`:@@home.expertDialog.ui.errorNotFound:（データが見つかりません）`,
    errorServer: $localize`:@@home.expertDialog.ui.errorServer:（サーバーエラー）`,
    errorNetwork: $localize`:@@home.expertDialog.ui.errorNetwork:（ネットワークエラー）`,
    backToList: $localize`:@@home.expertDialog.ui.backToList:一覧に戻る`,
  };

  @ViewChildren("mentionTextarea")
  mentionTextareaComponents!: QueryList<MentionTextareaComponent>;

  static LOAD_DIALOGS_INTERVAL_MSEC = 30000;

  // Signal ベースの状態管理
  dialogDataList = signal<DialogDocument[]>([]);
  isLoading = signal(true);
  mentionTargetUserNames: { [key: string]: string } = {};
  mentionTargetRecordNames: { [key: string]: string } = {};
  mentionTargetKinds: { [key: string]: MentionLinkKind } = {};
  loginUserId = "";
  selectedRecordId = "";

  // 2カラム: 選択された相談スレッド
  selectedDialog = signal<DialogDocument | null>(null);

  // フィルター管理
  private static readonly FILTER_STORAGE_KEY = 'expert-dialog-filter-open-only';
  showOnlyOpen = signal(localStorage.getItem(ExpertDialogInnerComponent.FILTER_STORAGE_KEY) === 'true');

  // 送信中状態
  isSending = signal(false);

  // 検索テキスト
  filterText = signal('');

  // 折りたたみ状態管理
  isComposeExpanded = signal(false);
  private expandedReplyIds = new Set<string>();

  // フィルタリングした相談リスト（新しい順：最新アクティビティ順）
  filteredDialogs = computed(() => {
    const allDialogs = this.dialogDataList();
    let filtered = allDialogs;

    if (this.showOnlyOpen()) {
      filtered = filtered.filter(d => this.getDialogStatus(d) === 'open');
    }

    const query = this.filterText().trim().toLowerCase();
    if (query) {
      filtered = filtered.filter(d => {
        const senderName = this.getFullName(d.meta.givenBy).toLowerCase();
        const questionText = MentionTextareaComponent.genMentionPlainText(d.data.questionText).toLowerCase();
        return senderName.includes(query) || questionText.includes(query);
      });
    }

    return [...filtered].sort((a, b) => {
      const timeA = getTimestamp(this.getLatestTime(a)) ?? Number.NEGATIVE_INFINITY;
      const timeB = getTimestamp(this.getLatestTime(b)) ?? Number.NEGATIVE_INFINITY;
      return timeB - timeA;
    });
  });

  // 選択中のダイアログの最新データを追跡
  readonly activeDialog = computed(() => {
    const selected = this.selectedDialog();
    if (!selected) return null;
    return this.dialogDataList().find(d => d.id === selected.id) ?? null;
  });

  // 状態別の件数（バッジ表示用）
  openCount = computed(() =>
    this.dialogDataList().filter(d => this.getDialogStatus(d) === 'open').length,
  );

  closedCount = computed(() =>
    this.dialogDataList().filter(d => this.getDialogStatus(d) === 'closed').length,
  );


  private _mentionTargetUserInfos: {
    [key: string]: { userId: string; lastName: string; firstName: string };
  } = {};
  private _selectedSection: Section | null = null;
  private _selectedUserId = "";
  private _clickListener!: (event: Event) => void;

  // スレッドプレビュー機能: 展開されたスレッドIDを追跡
  private expandedThreads = new Set<string>();
  private _lastFragment = "";
  // ステータス変更中のダイアログID（レース条件防止）
  private _pendingStatusDialogIds = new Set<string>();
  private dialogsLoadInflight: Promise<void> | null = null;
  private dialogsLoadInflightKey: string | null = null;
  private loadedDialogsKey: string | null = null;
  private dialogLoadRequestId = 0;
  private readonly mentionMembersCache = new Map<string, BelongsDocument[]>();
  private readonly mentionMembersInflight = new Map<string, Promise<BelongsDocument[]>>();

  ngOnInit() {
    let user = this.msalService.instance.getActiveAccount();
    this.loginUserId = JSON.stringify(user.idTokenClaims["sub"]);
    this.loginUserId = this.loginUserId.replace(/^"|"$/g, "");
    this.databaseCashe.parametersUpdated
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        void this._refreshFromCache();
      });
    // Fragment handling: popup mode reads from service, otherwise from route
    if (this.popupMode()) {
      effect(() => {
        this._lastFragment = this.popupWindowState.currentFragment() ?? '';
      }, { injector: this.injector });
    } else {
      this.route.fragment.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((fragment) => {
        this._lastFragment = fragment ?? '';
      });
    }

    // route.params.subscribe への Subscribe は this.common.parametersUpdated への Subscribe よりあとに行うこと
    this.route.params.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(async (params) => {
      try {
        const userId = params["userId"];
        if (userId) {
          await this.databaseCashe.setSelectedUserId(userId);
          this.databaseCashe.prepareUserRecordsList(userId, false);
          this.databaseCashe.prepareUserMediasList(userId, false);
        }

        // In popup mode, recordId comes from service (not route params)
        if (!this.popupMode()) {
          const recordId = params["recordId"];
          this.selectedRecordId = recordId ?? "";
        }
      } catch (error) {
        console.error(
          `[${ExpertDialogInnerComponent.name}] catch error in callback / error=${error}`,
        );
      }
    });

    // In popup mode, read recordId from service reactively
    if (this.popupMode()) {
      effect(() => {
        this.selectedRecordId = this.popupWindowState.currentRecordId();
        const currentId = this.selectedRecordId;
        if (currentId && !this.mentionTargetKinds[currentId]) {
          this.mentionTargetKinds = {
            ...this.mentionTargetKinds,
            [currentId]: this.resolveCurrentMentionKind(),
          };
        }
        this.changeDetectorRef.markForCheck();
      }, { injector: this.injector });
    }

    this._clickListener = (event) => {
      if (event.target instanceof HTMLAnchorElement) {
        event.preventDefault(); // デフォルトのリンク遷移を防ぐ

        const targetId = event.target.getAttribute("data-record-id");
        const dialogId = event.target.getAttribute("data-dialog-id");
        const responseIndex = event.target.getAttribute("data-response-index");
        if (targetId) {
          this._navigateToMentionTargetDetail(targetId, dialogId, responseIndex);
        }
      }
    };

    document.addEventListener("click", this._clickListener);
  }

  private async _refreshFromCache(): Promise<void> {
    try {
      this._selectedSection = this.databaseCashe.getSelectedSection();
      this._selectedUserId = this.databaseCashe.getSelectedUserId();

      if (!this._selectedSection || !this._selectedUserId) {
        return;
      }

      const roles = this.databaseCashe.getSelectedSectionRoles();
      const hasRole = this._determineHasRoles(roles);
      if (!hasRole) {
        return;
      }

      const recordsList = this.databaseCashe.getUserRecordsList();
      const mediasList = this.databaseCashe.getUserMediasList();
      await Promise.all([
        this._loadDialogs(this.dialogDataList().length === 0),
        this._fetchMentionTarget(recordsList ?? [], mediasList ?? []),
      ]);

      this._applyPendingFragment();
    } catch (error) {
      console.error(
        `[${ExpertDialogInnerComponent.name}] catch error in callback / error=${error}`,
      );
    }
  }

  private _applyPendingFragment(): void {
    if (this._lastFragment == null || this._lastFragment.length === 0) {
      return;
    }

    const fragment = this._lastFragment;
    const PREFIX = "dialog-content_";
    if (fragment.startsWith(PREFIX)) {
      const rest = fragment.substring(PREFIX.length);
      const lastDash = rest.lastIndexOf("-");
      if (lastDash > 0) {
        const dialogId = rest.substring(0, lastDash);
        const target = this.dialogDataList().find((d) => d.id === dialogId);
        if (target) {
          this.selectedDialog.set(target);
        }
      }
    }

    setTimeout(() => {
      this._scrollToElement(fragment);
    });
    this._lastFragment = "";
  }

  ngAfterViewInit() {
    // dialogQuestionInput はオンデマンド検索（_findQuestionInput）で取得するため、
    // ここでは初期化不要（compose が折りたたみ時に DOM にないため）
  }

  ngOnDestroy() {
    document.removeEventListener("click", this._clickListener);
  }

  async createDialogQuestion() {
    const questionInput = this._findQuestionInput();
    if (!questionInput || questionInput.isValid() == false) {
      return;
    }

    const { userMentions: mentions, content } =
      questionInput.confirmInput();

    this.isSending.set(true);
    try {
      var createdDialog = await this.createDialogService.createDialog({
        givenTo: this.databaseCashe.getSelectedUserId(),
        questionText: content,
      });
    } catch (error) {
      console.error(
        `[${ExpertDialogInnerComponent.name}] fail createDialog / error=${error}`,
      );
      this.snackBar.open(this._getErrorMessage(error), '', { duration: 5000, panelClass: 'snackbar-danger' });
      this.isSending.set(false);
      return;
    }

    const mentionToUserIdList = Object.keys(mentions);

    try {
      if (mentionToUserIdList != null && mentionToUserIdList.length > 0) {
        await this._createMentions(
          mentionToUserIdList,
          createdDialog.id,
          0,
          createdDialog.data.questionText,
        );
      }
    } catch (error) {
      console.error(
        `[${ExpertDialogInnerComponent.name}] fail createMention / error=${error}`,
      );
    }

    this.snackBar.open(this.i18n.message.sendSuccess, '', { duration: 3000, panelClass: 'snackbar-success' });

    try {
      await this._loadDialogs(true, true);
      this.clearDialogQuestion();
      // 新しく作成した相談を自動選択
      const newDialog = this.dialogDataList().find(d => d.id === createdDialog.id);
      if (newDialog) {
        this.selectedDialog.set(newDialog);
      }
    } finally {
      this.isSending.set(false);
    }
  }

  _determineHasRoles(roles) {
    let recordMeasurerRole = false;
    let recordRefererRole = false;

    if (roles != null && roles.length != 0) {
      if (roles.indexOf("MEASUREMENT") !== -1) {
        recordMeasurerRole = true;
      }

      if (roles.indexOf("REFER") !== -1) {
        recordRefererRole = true;
      }
    } else {
      console.warn(
        `[${ExpertDialogInnerComponent.name}] No roles for the  section`,
      );
    }

    return recordMeasurerRole || recordRefererRole;
  }

  public clearDialogQuestion() {
    const questionInput = this._findQuestionInput();
    if (questionInput) {
      questionInput.clearInput();
    }
    this.collapseCompose();
  }

  async _loadDialogs(showSpinner = true, force = false): Promise<void> {
    const key = this._getDialogLoadKey();
    if (!key) {
      return;
    }

    if (!force && this.loadedDialogsKey === key) {
      return;
    }

    if (!force && this.dialogsLoadInflight && this.dialogsLoadInflightKey === key) {
      return this.dialogsLoadInflight;
    }

    if (showSpinner) {
      this.isLoading.set(true);
    }

    const requestId = ++this.dialogLoadRequestId;
    const request = (async () => {
      try {
        const dialogList = await this.listDialogsService.listDialogs();
        if (requestId === this.dialogLoadRequestId && this._getDialogLoadKey() === key) {
          this.dialogDataList.set(dialogList);
          this.loadedDialogsKey = key;
        }
      } catch (error) {
        console.error(
          `[${ExpertDialogInnerComponent.name}] fail listDialogs / error=${error}`,
        );
      } finally {
        if (requestId === this.dialogLoadRequestId) {
          this.isLoading.set(false);
        }
      }
    })().finally(() => {
      if (this.dialogsLoadInflight === request) {
        this.dialogsLoadInflight = null;
        this.dialogsLoadInflightKey = null;
      }
    });

    this.dialogsLoadInflight = request;
    this.dialogsLoadInflightKey = key;
    return request;
  }

  private _getDialogLoadKey(): string | null {
    const sectionId =
      this._selectedSection?.sectionId ??
      this.databaseCashe.getSelectedSection()?.sectionId;
    const userId = this._selectedUserId || this.databaseCashe.getSelectedUserId();

    if (!sectionId || !userId) {
      return null;
    }

    return `${sectionId}|${userId}`;
  }

  /** 現在のDOM上から新規投稿用 MentionTextarea をオンデマンド検索 */
  private _findQuestionInput(): MentionTextareaComponent | null {
    return this.mentionTextareaComponents?.find(
      (comp) => comp.innerId === 'mention-textarea-0',
    ) ?? null;
  }

  /** 現在のDOM上から返信用 MentionTextarea をオンデマンド検索 */
  private _findResponseInput(dialogId: string): MentionTextareaComponent | null {
    return this.mentionTextareaComponents?.find(
      (comp) => comp.innerId === `mention-textarea-${dialogId}`,
    ) ?? null;
  }

  public async addDialogResponse(dialogData: DialogDocument) {
    const responseInput = this._findResponseInput(dialogData.id);
    if (!responseInput) {
      console.error(`[${ExpertDialogInnerComponent.name}] responseInput for ${dialogData.id} not found`);
      return;
    }

    if (responseInput.isValid() == false) {
      return;
    }

    const { userMentions: mentions, content } = responseInput.confirmInput();

    this.isSending.set(true);
    let updatedDialog;
    try {
      updatedDialog = await this.updateDialogService.updateDialog({
        dialogId: dialogData.id,
        newResponse: content,
      });
    } catch (error) {
      console.error(
        `[${ExpertDialogInnerComponent.name}] fail updateDialog / error=${error}`,
      );
      this.snackBar.open(this._getErrorMessage(error), '', { duration: 5000, panelClass: 'snackbar-danger' });
      this.isSending.set(false);
      return;
    }

    const mentionToUserIdList = Object.keys(mentions);

    this.snackBar.open(this.i18n.message.sendSuccess, '', { duration: 3000, panelClass: 'snackbar-success' });
    responseInput.clearInput();

    const index = updatedDialog.data.responses.length;
    try {
      if (mentionToUserIdList != null && mentionToUserIdList.length > 0) {
        await this._createMentions(
          mentionToUserIdList,
          updatedDialog.id,
          index,
          updatedDialog.data.responses[index - 1].responseText,
        );
      }
    } catch (error) {
      console.error(
        `[${ExpertDialogInnerComponent.name}] fail createMention / error=${error}`,
      );
    }

    try {
      await this._loadDialogs(true, true);
    } finally {
      this.isSending.set(false);
    }
  }

  public clearDialogResponse(dialogData: DialogDocument) {
    const responseInput = this._findResponseInput(dialogData.id);
    if (responseInput) {
      responseInput.clearInput();
    }
    this.collapseReply(dialogData.id);
  }

  async _fetchMentionTarget(recordsList: any[], mediasList: any[]) {
    try {
      const belongsList = await this._loadMentionMembers(this._selectedSection.sectionId);

      this._mentionTargetUserInfos = {};
      for (let i = 0; i < belongsList.length; i++) {
        var userId = belongsList[i].user.userId;
        var lastName = belongsList[i].user.lastName;
        var firstName = belongsList[i].user.firstName;

        this._mentionTargetUserInfos[userId] = {
          userId: userId,
          lastName: lastName,
          firstName: firstName,
        };
      }

      this.mentionTargetUserNames = Object.entries(this._mentionTargetUserInfos)
        .filter(([userId]) => userId !== this.loginUserId)
        .sort(([, userA], [, userB]) => {
          const nameA = `${userA.lastName} ${userA.firstName}`;
          const nameB = `${userB.lastName} ${userB.firstName}`;
          return nameA.localeCompare(nameB, "ja");
        })
        .reduce((acc, [userId, user]) => {
          acc[userId] = `${user.lastName} ${user.firstName}`;
          return acc;
        }, {});

      const recordTargets = [...(recordsList ?? [])]
        .sort(
          (a, b) =>
            (getTimestamp(b?.data?.createdAt) ?? Number.NEGATIVE_INFINITY)
              - (getTimestamp(a?.data?.createdAt) ?? Number.NEGATIVE_INFINITY),
        )
        .reduce((acc, record) => {
          if (!record?.id) {
            return acc;
          }
          let recordType = record?.data?.recordType ?? this.ui.record;
          if (recordType === "MEAL_MONITORING") {
            recordType = "MONITORING";
          } else if (recordType === "MWST_G") {
            recordType = "MWST";
          } else if (recordType === "FOOD_TEST_G") {
            recordType = "FOOD TEST";
          }
          acc[record.id] =
            recordType + " " + this._convertToLocalTime(record?.data?.createdAt);
          return acc;
        }, {});

      const mediaTargets = [...(mediasList ?? [])]
        .sort(
          (a, b) =>
            (getTimestamp(b?.data?.createdAt) ?? Number.NEGATIVE_INFINITY)
              - (getTimestamp(a?.data?.createdAt) ?? Number.NEGATIVE_INFINITY),
        )
        .reduce((acc, media) => {
          if (!media?.id) {
            return acc;
          }
          acc[media.id] = this.formatMediaMentionName(media);
          return acc;
        }, {});

      this.mentionTargetRecordNames = {
        ...recordTargets,
        ...mediaTargets,
      };
      this.mentionTargetKinds = {
        ...Object.keys(recordTargets).reduce((acc, id) => {
          acc[id] = 'record';
          return acc;
        }, {} as { [key: string]: MentionLinkKind }),
        ...Object.keys(mediaTargets).reduce((acc, id) => {
          acc[id] = 'media';
          return acc;
        }, {} as { [key: string]: MentionLinkKind }),
      };
    } catch (error) {
      console.error(
        `[${ExpertDialogInnerComponent.name}] fail listMembers / error=${error}`,
      );
      throw error;
    }

  }

  private async _loadMentionMembers(sectionId: string): Promise<BelongsDocument[]> {
    const cached = this.mentionMembersCache.get(sectionId);
    if (cached) {
      return cached;
    }

    const inflight = this.mentionMembersInflight.get(sectionId);
    if (inflight) {
      return inflight;
    }

    const request = lastValueFrom(
      this.listMembersService.listMembers(sectionId, "STAFF", true),
    )
      .then((res) => {
        const belongsList = (res as BelongsDocument[]).filter((belong) => {
          return this._determineHasRoles(belong.permissions.role);
        });
        this.mentionMembersCache.set(sectionId, belongsList);
        return belongsList;
      })
      .finally(() => {
        this.mentionMembersInflight.delete(sectionId);
      });

    this.mentionMembersInflight.set(sectionId, request);
    return request;
  }

  async _createMentions(
    mentionToUserIdList: string[],
    dialogId: string,
    index: number,
    mentionedContent: string,
  ) {
    var toFirstNameList: string[] = [];
    var toLastNameList: string[] = [];

    if (mentionToUserIdList == null || mentionToUserIdList.length == 0) {
      throw new Error(
        `[${ExpertDialogInnerComponent.name}] mentionToUserIdList is empty`,
      );
    }

    for (let i = 0; i < mentionToUserIdList.length; i++) {
      toFirstNameList.push(
        this._mentionTargetUserInfos[mentionToUserIdList[i]].firstName,
      );
      toLastNameList.push(
        this._mentionTargetUserInfos[mentionToUserIdList[i]].lastName,
      );
    }

    try {
      var createdMention = await this.createMentionService.createMention(
        this._selectedSection.sectionId,
        this._selectedSection.sectionName,
        this._selectedUserId,
        this.loginUserId,
        this._mentionTargetUserInfos[this.loginUserId].firstName,
        this._mentionTargetUserInfos[this.loginUserId].lastName,
        mentionToUserIdList,
        toFirstNameList,
        toLastNameList,
        dialogId,
        index,
        mentionedContent,
      );
    } catch (error) {
      console.error(
        `[${ExpertDialogInnerComponent.name}] fail createMention / error=${error}`,
      );
      throw error;
    }
    return createdMention;
  }

  _scrollToElement(id: string): void {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
    } else {
      console.warn(
        `[${ExpertDialogInnerComponent.name}] scroll to element with ID '${id}' not found`,
      );
    }
  }

  _navigateToMentionTargetDetail(
    targetId: string,
    dialogId: string | null,
    responseIndex: string | null,
  ) {
    if (!this._selectedSection || !this._selectedUserId) {
      return;
    }
    const fragment = dialogId != null && responseIndex != null
      ? `dialog-content_${dialogId}-${responseIndex}`
      : undefined;
    const targetKind = this.mentionTargetKinds[targetId] ?? 'record';

    if (targetKind === 'media') {
      this.router.navigate(
        [
          "home",
          this._selectedSection.sectionId,
          "users",
          this._selectedUserId,
          "medias",
          targetId,
        ],
        {
          queryParams: { from: 'dialogs' },
          fragment,
        },
      );
      return;
    }

    this.router.navigate(
      [
        "home",
        this._selectedSection.sectionId,
        "users",
        this._selectedUserId,
        "records",
        targetId,
      ],
      {
        fragment,
      },
    );
  }

  /** エラーからユーザー向けメッセージを生成 */
  private _getErrorMessage(error: unknown): string {
    const base = this.i18n.message.sendFailed;

    // HttpErrorResponse が文字列化されたケース (interceptor 経由)
    if (typeof error === 'string') {
      if (error.includes('403') || error.includes('Forbidden')) {
        return `${base} ${this.ui.errorNoPermission}`.trim();
      }
      if (error.includes('401') || error.includes('Unauthorized')) {
        return `${base} ${this.ui.errorAuth}`.trim();
      }
      if (error.includes('404') || error.includes('Not Found')) {
        return `${base} ${this.ui.errorNotFound}`.trim();
      }
      if (error.includes('500') || error.includes('Internal Server')) {
        return `${base} ${this.ui.errorServer}`.trim();
      }
      if (error.includes('0') || error.includes('Unknown')) {
        return `${base} ${this.ui.errorNetwork}`.trim();
      }
    }

    // HttpErrorResponse オブジェクトのケース
    if (error && typeof error === 'object' && 'status' in error) {
      const status = (error as { status: number }).status;
      if (status === 403) return `${base} ${this.ui.errorNoPermission}`.trim();
      if (status === 401) return `${base} ${this.ui.errorAuth}`.trim();
      if (status === 404) return `${base} ${this.ui.errorNotFound}`.trim();
      if (status === 0) return `${base} ${this.ui.errorNetwork}`.trim();
      if (status >= 500) return `${base} ${this.ui.errorServer}`.trim();
    }

    return base;
  }

  _convertToLocalTime(isoDate: string): string {
    const date = parseDateInput(isoDate);
    if (!date) {
      return "";
    }
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0"); // 月は0始まり
    const day = String(date.getDate()).padStart(2, "0");
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    const seconds = String(date.getSeconds()).padStart(2, "0");
    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
  }

  public genMentionHtml(
    contentWithTags: string,
    ownUserId: string,
    dialogId: string,
    responseIndex: number,
  ) {
    return MentionTextareaComponent.genMentionHtml(
      this.sanitizer,
      contentWithTags,
      ownUserId,
      dialogId,
      responseIndex,
    );
  }

  public mentionToCurrentRecord(dialogId: string) {
    let targetMentionTextarea: MentionTextareaComponent | null;
    if (dialogId === '0') {
      targetMentionTextarea = this._findQuestionInput();
    } else {
      targetMentionTextarea = this._findResponseInput(dialogId);
    }

    if (targetMentionTextarea == null) {
      console.error(
        `[${ExpertDialogInnerComponent.name}] targetMentionTextarea is null`,
      );
      return;
    }

    if (!this.selectedRecordId) {
      return;
    }

    const mentionKind = this.resolveCurrentMentionKind();
    const selectedRecordName = this.resolveCurrentMentionName(mentionKind);

    if (!this.mentionTargetRecordNames[this.selectedRecordId]) {
      this.mentionTargetRecordNames = {
        ...this.mentionTargetRecordNames,
        [this.selectedRecordId]: selectedRecordName,
      };
    }
    if (!this.mentionTargetKinds[this.selectedRecordId]) {
      this.mentionTargetKinds = {
        ...this.mentionTargetKinds,
        [this.selectedRecordId]: mentionKind,
      };
    }

    targetMentionTextarea.insertItem(
      "#",
      this.selectedRecordId,
      selectedRecordName,
    );
  }

  public getMentionCurrentLabel(): string {
    return this.resolveCurrentMentionKind() === 'media'
      ? this.ui.mentionCurrentMedia
      : this.ui.mentionCurrentRecord;
  }

  private resolveCurrentMentionKind(): MentionLinkKind {
    const selectedId = this.selectedRecordId;
    if (selectedId && this.mentionTargetKinds[selectedId]) {
      return this.mentionTargetKinds[selectedId];
    }
    const url = this.router.url.split('?')[0];
    return /\/medias\/[^/]+/.test(url) ? 'media' : 'record';
  }

  private resolveCurrentMentionName(mentionKind: MentionLinkKind): string {
    const selectedId = this.selectedRecordId;
    const fallback = mentionKind === 'media'
      ? this.ui.currentMedia
      : this.ui.currentRecord;
    if (!selectedId) {
      return fallback;
    }

    const knownName = this.mentionTargetRecordNames[selectedId];
    if (knownName) {
      return knownName;
    }

    const popupCurrentId = this.popupWindowState.currentRecordId();
    const popupCurrentLabel = this.popupWindowState.currentRecordLabel();
    if (popupCurrentId === selectedId && popupCurrentLabel) {
      return popupCurrentLabel;
    }

    const computedName = mentionKind === 'media'
      ? this.buildMediaMentionName(selectedId)
      : this.buildRecordMentionName(selectedId);
    return computedName ?? fallback;
  }

  private buildRecordMentionName(recordId: string): string | null {
    const records = this.databaseCashe.getUserRecordsList() ?? [];
    const record = records.find((item: any) => item?.id === recordId);
    if (!record?.id) {
      return null;
    }
    const recordType = this.normalizeRecordTypeLabel(record?.data?.recordType);
    const createdAt = this._convertToLocalTime(record?.data?.createdAt);
    return [recordType, createdAt].filter(Boolean).join(' ');
  }

  private buildMediaMentionName(mediaId: string): string | null {
    const medias = this.databaseCashe.getUserMediasList() ?? [];
    const media = medias.find((item: any) => item?.id === mediaId);
    if (!media?.id) {
      return null;
    }
    return this.formatMediaMentionName(media);
  }

  private formatMediaMentionName(media: any): string {
    const method = media?.data?.method ?? this.ui.mediaFallback;
    const createdAt = this._convertToLocalTime(media?.data?.createdAt);
    const isVideo = media?.data?.mediaType === 'Video';

    // 要望: 静止画は「静止画 タグ名 日時」の順で表示する
    if (!isVideo) {
      return [this.ui.image, method, createdAt].filter(Boolean).join(' ');
    }

    return [method, this.ui.video, createdAt].filter(Boolean).join(' ');
  }

  private normalizeRecordTypeLabel(recordType: string | undefined): string {
    let normalizedType = recordType ?? this.ui.record;
    if (normalizedType === 'MEAL_MONITORING') {
      normalizedType = 'MONITORING';
    } else if (normalizedType === 'MWST_G') {
      normalizedType = 'MWST';
    } else if (normalizedType === 'FOOD_TEST_G') {
      normalizedType = 'FOOD TEST';
    }
    return normalizedType;
  }

  public convertToResponseIndex(index: number): number {
    return index + 1;
  }

  public shouldExpandThread(dialogData: DialogDocument): boolean {
    const responseCount = dialogData.data.responses?.length ?? 0;
    // ルール: 1件以下は展開、2件以上は折りたたみ
    return responseCount <= 1;
  }

  // =============================================================================
  // スレッドプレビュー機能（カスタム実装）
  // =============================================================================

  /**
   * 返信プレビューを表示すべきか（2件以上の返信がある場合）
   */
  public shouldShowPreview(dialogData: DialogDocument): boolean {
    const responseCount = dialogData.data.responses?.length ?? 0;
    return responseCount >= 2;
  }

  /**
   * スレッドが展開されているか
   */
  public isThreadExpanded(dialogId: string): boolean {
    return this.expandedThreads.has(dialogId);
  }

  /**
   * スレッドの展開/折りたたみを切り替え
   */
  public toggleThread(dialogId: string): void {
    if (this.expandedThreads.has(dialogId)) {
      this.expandedThreads.delete(dialogId);
    } else {
      this.expandedThreads.add(dialogId);
    }
    this.changeDetectorRef.markForCheck();
  }

  // =============================================================================
  // フィルター管理
  // =============================================================================

  onOpenFilterChange(event: MatChipSelectionChange): void {
    this.showOnlyOpen.set(event.selected);
    localStorage.setItem(ExpertDialogInnerComponent.FILTER_STORAGE_KEY, String(event.selected));
  }

  // =============================================================================
  // ステータス管理（Optimistic UI + Deferred API Call）
  // =============================================================================

  /** dialogStatus を安全に取得（null/undefined 対応、小文字正規化） */
  getDialogStatus(dialog: DialogDocument): string {
    const raw = dialog.meta?.dialogStatus?.status?.toLowerCase() || 'open';
    // バックエンドは "Close" を返すが、フロントエンドでは "closed" で統一
    return raw === 'close' ? 'closed' : raw;
  }

  /** 投稿者のみが状態変更可能 */
  canChangeStatus(dialog: DialogDocument): boolean {
    return this.loginUserId === dialog.meta?.givenBy?.userId;
  }

  /** Optimistic UI: ローカル即座更新 → Toast → API呼び出し */
  toggleStatusLocal(dialog: DialogDocument): void {
    // レース条件防止: 同一ダイアログのステータス変更が進行中なら無視
    if (this._pendingStatusDialogIds.has(dialog.id)) {
      return;
    }
    this._pendingStatusDialogIds.add(dialog.id);

    const currentList = this.dialogDataList();
    const currentStatus = this.getDialogStatus(dialog);
    // API送信用: バックエンドは 'open' / 'close' を期待（'closed' ではない）
    const newStatus = currentStatus === 'open' ? 'close' : 'open';
    // UI表示用: フロントエンドでは 'closed' で統一
    const displayStatus = newStatus === 'close' ? 'closed' : 'open';

    // 即座にローカル状態を更新
    const updatedList = currentList.map(d => {
      if (d.id === dialog.id) {
        const prevStatus: DialogStatus = d.meta.dialogStatus ?? { status: 'open', nice: false };
        return {
          ...d,
          meta: {
            ...d.meta,
            dialogStatus: {
              ...prevStatus,
              status: newStatus,
            },
          },
        };
      }
      return d;
    });
    this.dialogDataList.set(updatedList);

    // Toast 通知（Undo 機能付き）
    const message = displayStatus === 'closed'
      ? this.ui.statusDone
      : this.ui.statusReopened;

    const snackBarRef = this.snackBar.open(message, this.ui.undo, { duration: 5000 });

    // Undo ボタンが押されたら元に戻す
    snackBarRef.onAction().subscribe(() => {
      this.dialogDataList.set(currentList);
    });

    // Toast 終了時に API 呼び出し（Undo されなかった場合のみ）
    snackBarRef.afterDismissed().subscribe((dismissInfo) => {
      this._pendingStatusDialogIds.delete(dialog.id);

      if (!dismissInfo.dismissedByAction) {
        const payload = {
          dialogId: dialog.id,
          status: newStatus,
        };
        this.updateDialogService.updateDialog(payload).catch((_error) => {
          console.error('Failed to update status', _error);
          this.dialogDataList.set(currentList);
          this.snackBar.open(this.ui.statusUpdateFailed, '', { duration: 3000, panelClass: 'snackbar-danger' });
        });
      }
    });
  }

  getStatusButtonLabel(status: string): string {
    return status === 'open' ? this.ui.markResolved : this.ui.reopenConsultation;
  }

  getStatusLabel(status: string): string {
    return status === 'open' ? this.ui.statusOpen : this.ui.statusClosed;
  }

  getStatusButtonIcon(status: string): string {
    return status === 'open' ? '✓' : '↻';
  }

  // =============================================================================
  // 新規相談フォームの展開/折りたたみ
  // =============================================================================

  toggleCompose(): void {
    this.isComposeExpanded.update(v => !v);
  }

  collapseCompose(): void {
    this.isComposeExpanded.set(false);
  }

  // =============================================================================
  // 返信フォームの展開/折りたたみ
  // =============================================================================

  isReplyExpanded(dialogId: string): boolean {
    return this.expandedReplyIds.has(dialogId);
  }

  toggleReply(dialogId: string): void {
    if (this.expandedReplyIds.has(dialogId)) {
      this.expandedReplyIds.delete(dialogId);
    } else {
      this.expandedReplyIds.add(dialogId);
    }
    this.changeDetectorRef.markForCheck();
  }

  collapseReply(dialogId: string): void {
    this.expandedReplyIds.delete(dialogId);
    this.changeDetectorRef.markForCheck();
  }

  // =============================================================================
  // 2カラム: 選択・ヘルパー
  // =============================================================================

  selectDialog(dialog: DialogDocument): void {
    this.selectedDialog.set(dialog);
  }

  deselectDialog(): void {
    this.selectedDialog.set(null);
  }

  isDialogSelected(dialog: DialogDocument): boolean {
    return this.selectedDialog()?.id === dialog.id;
  }

  /** 質問テキストからメンションタグを除去してスニペットを返す */
  getQuestionSnippet(dialog: DialogDocument): string {
    return MentionTextareaComponent.genMentionPlainText(
      dialog.data.questionText,
    );
  }

  /** 返信数を取得 */
  getReplyCount(dialog: DialogDocument): number {
    return dialog.data.responses?.length ?? 0;
  }

  /** 最新の日時を取得（返信がある場合は最新返信の日時） */
  getLatestTime(dialog: DialogDocument): string {
    const responses = dialog.data.responses;
    if (responses && responses.length > 0) {
      return responses[responses.length - 1].createdAt;
    }
    return dialog.data.createdAt;
  }

  // =============================================================================
  // Avatar helpers
  // =============================================================================

  getInitials(user: MetaDialogUser): string {
    return user.lastName ? user.lastName.charAt(0) : "?";
  }

  getFullName(user: MetaDialogUser): string {
    return user.lastName + " " + user.firstName;
  }

  isOwnMessage(userId: string): boolean {
    return this.loginUserId === userId;
  }
}
